/**
 * Pure rally segmentation over a list of contact events.
 *
 * A "rally" is a run of contacts where consecutive contacts are no more than
 * `endGapMs` apart. When the gap exceeds that, the rally is closed and a new
 * one starts on the next contact. Intentionally framework-free: no pose,
 * ball, or court required — just contact timestamps.
 *
 * Not-yet-implemented (tracked inline):
 *   - Serve detection as a rally-boundary: a fresh serve pose mid-gap should
 *     also split rallies, regardless of timing. Requires the shot classifier
 *     to emit `serve` reliably (Sprint 4).
 */

import type { ContactEvent } from '../types.js';

export interface Rally {
  /** Index in the input array of the first contact of the rally. */
  readonly startContactIdx: number;
  /** Index in the input array of the last contact of the rally (inclusive). */
  readonly endContactIdx: number;
  readonly startTimeMs: number;
  readonly endTimeMs: number;
  /** Number of contacts in the rally (endIdx - startIdx + 1). */
  readonly contactCount: number;
}

export interface DetectRalliesOptions {
  /**
   * Gap between successive contacts that ends a rally. Default 3500 ms — a
   * typical club rally has 2-3 shots per second between players, and a gap
   * that long reliably marks the point ending (or a service toss). Too low
   * (1.5 s) splits long baseline exchanges; too high (6 s) bridges points
   * that were clearly separate.
   */
  readonly endGapMs?: number;
  /**
   * Minimum contacts for a run to be reported. Rallies with fewer are
   * dropped — keeps single stray contacts (net touches, practice bounces)
   * out of the UI. Default 1 (no filtering).
   */
  readonly minContactsPerRally?: number;
}

const DEFAULTS = {
  endGapMs: 3500,
  minContactsPerRally: 1,
} as const;

/**
 * Segment a chronologically-ordered list of contacts into rallies. Input is
 * expected to already be sorted by `timeMs`; behavior is undefined otherwise.
 */
export function detectRallies(
  contacts: readonly ContactEvent[],
  opts: DetectRalliesOptions = {},
): Rally[] {
  const endGapMs = opts.endGapMs ?? DEFAULTS.endGapMs;
  const minContacts = Math.max(1, opts.minContactsPerRally ?? DEFAULTS.minContactsPerRally);
  const n = contacts.length;
  if (n === 0) return [];

  const rallies: Rally[] = [];
  let startIdx = 0;
  for (let i = 1; i < n; i++) {
    const prev = contacts[i - 1]!;
    const cur = contacts[i]!;
    if (cur.timeMs - prev.timeMs > endGapMs) {
      pushIf(rallies, contacts, startIdx, i - 1, minContacts);
      startIdx = i;
    }
  }
  pushIf(rallies, contacts, startIdx, n - 1, minContacts);
  return rallies;
}

function pushIf(
  out: Rally[],
  contacts: readonly ContactEvent[],
  startIdx: number,
  endIdx: number,
  minContacts: number,
): void {
  const count = endIdx - startIdx + 1;
  if (count < minContacts) return;
  const start = contacts[startIdx]!;
  const end = contacts[endIdx]!;
  out.push({
    startContactIdx: startIdx,
    endContactIdx: endIdx,
    startTimeMs: start.timeMs,
    endTimeMs: end.timeMs,
    contactCount: count,
  });
}
